import type {
  FamilyDebtRiskOutput,
  FtbEligibilityGapOutput,
  FtbEntitlementOutput,
} from "./types.js";

// Plain-English family-payments copy. Lives in the rules package so every
// surface renders identical wording from one source — a REGULATORY requirement,
// not just DRY. FTB is information, not advice (spec §4.1, §7.5; validation §3):
//
//  - No "save $X" / "you should claim" / "you're missing money".
//  - FTB is a Services Australia payment, NOT a financial product — $0 affiliate,
//    NO handoff of any kind off these surfaces.
//  - Every output cites Services Australia / DSS and names Services Australia as
//    the authoritative finaliser (entitlements are confirmed at balancing).
//  - The eligibility-gap pitch is "easy to check", NOT "many are missing it"
//    (the take-up-gap leakage stat is UNVERIFIED).
//  - The supplement is capped at $80,000 ATI; copy NEVER implies it persists
//    above the cap.
//  - The debt-risk figure is framed "at least this much, possibly more", FTB and
//    CCS shown separately, never a falsely reassuring point estimate.
//
// Pure string-builders, unit-tested.

/** AUD, en-AU formatting, 2dp. */
export function formatAud(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return `A$${rounded.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** The mandatory disclaimer every family-payments surface renders. */
export const FAMILY_PAYMENTS_DISCLAIMER =
  "Educational tool. Not financial advice. This is an estimate based on the figures you entered — your actual entitlement is determined by Services Australia. Check your eligibility and lodge any claim at servicesaustralia.gov.au.";

/** Source line, composed from the rate period in force. */
export function sourceBullet(periodLabel: string, verifiedAt: string): string {
  return `These figures use the Services Australia Family Tax Benefit rules for ${periodLabel} (last verified ${verifiedAt}). FTB rates and income-test thresholds are indexed twice a year (20 March and 20 September) — always check servicesaustralia.gov.au.`;
}

/* ------------------------------------------------------------------------- */
/* Surface 1 — ftbEntitlement                                                */
/* ------------------------------------------------------------------------- */

/** Headline: states the indicative annual figure, never as a "saving". */
export function entitlementHeadline(output: FtbEntitlementOutput): string {
  if (output.estimatedAnnualTotal === 0) {
    return "A$0 — no Family Tax Benefit applies on these figures";
  }
  return `Your estimated Family Tax Benefit on these figures: ${formatAud(
    output.estimatedAnnualTotal,
  )} for the year (Services Australia finalises the actual amount at balancing).`;
}

/** How Part A was derived — states the taper stage, no advice. */
export function partABullet(output: FtbEntitlementOutput): string {
  const a = output.partA;
  const stage: Record<FtbEntitlementOutput["partA"]["taperStage"], string> = {
    max: "the maximum rate applies (your income is at or below the lower income free area)",
    "lower-taper":
      "the maximum rate is reduced because your income is above the lower income free area",
    base: "the base rate applies (your income is above the lower free area but the base rate still pays)",
    "higher-taper":
      "the base rate is reduced because your income is above the higher income free area",
    nil: "no Part A is payable on these figures",
  };
  return `FTB Part A: ${formatAud(a.ratePerFortnight)} per fortnight — ${stage[a.taperStage]}.`;
}

/** How Part B was derived. */
export function partBBullet(output: FtbEntitlementOutput): string {
  const b = output.partB;
  if (!b.payable) {
    if (b.nilReason === "over-primary-limit") {
      return "FTB Part B: not payable on these figures — the primary earner's income is above the Part B limit.";
    }
    if (b.nilReason === "secondary-taper-out") {
      return "FTB Part B: not payable on these figures — the secondary earner's income is above the Part B cut-out.";
    }
    return "FTB Part B: not included in this estimate.";
  }
  return `FTB Part B: ${formatAud(b.ratePerFortnight)} per fortnight.`;
}

/** How the supplements were treated — flags the $80k cap explicitly. */
export function supplementBullet(output: FtbEntitlementOutput): string {
  if (output.supplement.overSupplementCap) {
    return "FTB supplements: not available on these figures — the end-of-year supplements are not paid where family income is above A$80,000. They do not apply above that income.";
  }
  const parts: string[] = [];
  if (output.supplement.partA > 0) {
    parts.push(`Part A supplement ${formatAud(output.supplement.partA)}`);
  }
  if (output.supplement.partB > 0) {
    parts.push(`Part B supplement ${formatAud(output.supplement.partB)}`);
  }
  if (parts.length === 0) {
    return "FTB supplements: none apply on these figures.";
  }
  return `FTB supplements (paid after balancing): ${parts.join(", ")}.`;
}

export function entitlementBullets(
  output: FtbEntitlementOutput,
  periodLabel: string,
  verifiedAt: string,
): string[] {
  return [
    partABullet(output),
    partBBullet(output),
    supplementBullet(output),
    sourceBullet(periodLabel, verifiedAt),
    FAMILY_PAYMENTS_DISCLAIMER,
  ];
}

/* ------------------------------------------------------------------------- */
/* Surface 2 — ftbEligibilityGap                                             */
/* ------------------------------------------------------------------------- */

/**
 * Eligibility-gap headline — "easy to check", NOT "you're missing money". The
 * pitch is reframed away from an unverified leakage stat: it simply notes the
 * base rate extends past the cutoff many people assume.
 */
export function eligibilityGapHeadline(output: FtbEligibilityGapOutput): string {
  if (!output.aboveMaxRateCutoff) {
    return "On these figures your income is at or below the maximum-rate cutoff — it's quick to check your Family Tax Benefit Part A with Services Australia.";
  }
  if (output.baseRateMayApply) {
    return `Your income is above the ${formatAud(
      output.maxRateCutoff,
    )} maximum-rate cutoff, but FTB Part A's base rate can still be payable above that point — it's easy to check whether you're eligible.`;
  }
  return "On these figures your income is above the point where FTB Part A's base rate cuts out — but eligibility depends on your circumstances, so it's still quick to check with Services Australia.";
}

/** Supplement caveat — copy MUST NOT imply the supplement persists above $80k. */
export function eligibilityGapSupplementBullet(
  output: FtbEligibilityGapOutput,
): string {
  if (output.supplementUnavailable) {
    return "Note: the end-of-year FTB supplement is not paid where family income is above A$80,000 — it does not apply above that income, even if the base rate does.";
  }
  return "The end-of-year FTB supplement is paid where family income is at or below A$80,000.";
}

export function eligibilityGapBullets(
  output: FtbEligibilityGapOutput,
  periodLabel: string,
  verifiedAt: string,
): string[] {
  return [
    eligibilityGapHeadline(output),
    eligibilityGapSupplementBullet(output),
    sourceBullet(periodLabel, verifiedAt),
    FAMILY_PAYMENTS_DISCLAIMER,
  ];
}

/* ------------------------------------------------------------------------- */
/* Surface 3 — familyDebtRisk                                                */
/* ------------------------------------------------------------------------- */

/**
 * Debt-risk headline — "at least this much, possibly more", never a falsely
 * reassuring point estimate. Protective framing; nothing to sell.
 */
export function debtRiskHeadline(output: FamilyDebtRiskOutput): string {
  if (!output.underEstimated) {
    return "On these figures your lodged family income estimate is at or above your projected actual income — there's no projected reconciliation debt. Services Australia confirms this at balancing.";
  }
  return `If your actual income comes in higher than your lodged estimate, you may owe at least ${formatAud(
    output.combinedDebtExposure,
  )} across FTB and Child Care Subsidy combined — possibly more. Services Australia works out the actual figure at balancing.`;
}

/** FTB and CCS exposures, ALWAYS shown separately (panel ship-condition). */
export function debtRiskComponentBullets(output: FamilyDebtRiskOutput): string[] {
  if (!output.underEstimated) {
    return ["No projected FTB or Child Care Subsidy reconciliation debt on these figures."];
  }
  return [
    `FTB side: at least ${formatAud(
      output.ftbDebtExposure,
    )} — FTB has no withholding buffer, so any overpayment is carried in full.`,
    `Child Care Subsidy side: at least ${formatAud(
      output.ccsDebtExposure,
    )} after your ${(output.ccsWithholdingRate * 100).toFixed(0)}% withholding (${formatAud(
      output.ccsBufferCredit,
    )}) is credited. CCS withholds a portion of each payment as a buffer; FTB does not.`,
  ];
}

export function debtRiskBullets(
  output: FamilyDebtRiskOutput,
  periodLabel: string,
  verifiedAt: string,
): string[] {
  return [
    debtRiskHeadline(output),
    ...debtRiskComponentBullets(output),
    sourceBullet(periodLabel, verifiedAt),
    FAMILY_PAYMENTS_DISCLAIMER,
  ];
}
